import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Coins, Loader2, Plus, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { promptDialog, toast } from '../../lib/dialogs'
import { formatCents, parseEurosToCents } from '../../lib/money'
import { currentLocaleTag } from '../../lib/formatting'
import {
  PaymentMethodLabel,
  type FundsProvisionDto,
  type FundsProvisionListResponse,
  type PaymentMethodT,
} from '@jr2os/shared'

interface Props {
  clientId: string
  opportunityId?: string
  /** Si true, oculta el botón crear (solo lectura). */
  readOnly?: boolean
}

export default function FundsProvisionPanel({ clientId, opportunityId, readOnly }: Props) {
  const { t } = useTranslation(['billing', 'common'])
  const [items, setItems] = useState<FundsProvisionDto[]>([])
  const [availableTotal, setAvailableTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ clientId })
      if (opportunityId) params.set('opportunityId', opportunityId)
      const resp = await api.get<FundsProvisionListResponse>(
        `/funds-provisions?${params.toString()}`
      )
      setItems(resp.items)
      setAvailableTotal(resp.availableTotalCents)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common:errors.generic'))
    } finally {
      setLoading(false)
    }
  }, [clientId, opportunityId, t])

  useEffect(() => {
    void reload()
  }, [reload])

  const onRefund = async (p: FundsProvisionDto) => {
    const txt = await promptDialog({
      title: t('fundsProvision.refundBalance'),
      message: t('fundsProvision.refundPrompt', { balance: formatCents(p.availableCents) }),
      defaultValue: (p.availableCents / 100).toFixed(2).replace('.', ','),
      confirmLabel: t('fundsProvision.refund'),
    })
    if (!txt) return
    const cents = parseEurosToCents(txt)
    if (cents <= 0 || cents > p.availableCents) {
      toast.error(t('fundsProvision.invalidAmount'))
      return
    }
    try {
      await api.post(`/funds-provisions/${p.id}/refund`, { amountCents: cents })
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      toast.error(body?.error ?? (err instanceof Error ? err.message : t('common:errors.generic')))
    }
  }

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <Coins size={14} className="text-jr2-700 dark:text-jr2-300" />
          {t('fundsProvision.title')}
          {items.length > 0 && (
            <span className="text-xs t-faint font-normal">({items.length})</span>
          )}
        </h3>
        {!readOnly && (
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-jr2-700 dark:text-jr2-200 bg-jr2-500/15 hover:bg-jr2-500/25 flex items-center gap-1"
          >
            <Plus size={12} /> {t('fundsProvision.receive')}
          </button>
        )}
      </div>

      <div className="text-xs t-muted mb-3">
        {t('fundsProvision.availableBalance')}:{' '}
        <span className="font-mono t-primary font-semibold text-base">
          {formatCents(availableTotal)}
        </span>
      </div>

      {error && (
        <p className="text-xs text-rose-700 dark:text-rose-300 mb-3">{error}</p>
      )}

      {loading ? (
        <div className="t-faint text-xs flex items-center gap-1.5 py-2">
          <Loader2 size={11} className="animate-spin" /> {t('common:loading')}
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs t-faint py-3 text-center">{t('fundsProvision.empty')}</p>
      ) : (
        <ul className="divide-y divide-base/40 text-sm">
          {items.map((p) => (
            <li key={p.id} className="py-2.5">
              <div className="flex items-baseline gap-2">
                <span className="t-secondary text-xs">
                  {new Date(p.receivedAt).toLocaleDateString(currentLocaleTag())}
                </span>
                <span className="ml-auto font-mono t-primary font-semibold">
                  {formatCents(p.amountCents)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11px] t-faint mt-0.5">
                <span>{t('enums:PaymentMethod.' + p.method)}</span>
                {p.reference && <span className="font-mono">· {p.reference}</span>}
                <span className="ml-auto">
                  {t('fundsProvision.available')}:{' '}
                  <span className="t-primary font-mono">{formatCents(p.availableCents)}</span>
                </span>
              </div>
              {p.description && (
                <p className="text-xs t-secondary mt-1">{p.description}</p>
              )}
              <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                <StatusBadge status={p.status} />
                {p.appliedCents > 0 && (
                  <span className="text-emerald-700 dark:text-emerald-300">
                    {t('fundsProvision.applied')}: {formatCents(p.appliedCents)}
                  </span>
                )}
                {p.refundedCents > 0 && (
                  <span className="text-amber-700 dark:text-amber-300">
                    {t('fundsProvision.refunded')}: {formatCents(p.refundedCents)}
                  </span>
                )}
                {!readOnly && p.availableCents > 0 && (
                  <button
                    type="button"
                    onClick={() => void onRefund(p)}
                    className="ml-auto t-secondary hover:text-amber-700 dark:hover:text-amber-300 inline-flex items-center gap-1"
                    title={t('fundsProvision.refundBalance')}
                  >
                    <RotateCcw size={10} /> {t('fundsProvision.refund')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showNew && (
        <NewProvisionModal
          clientId={clientId}
          opportunityId={opportunityId}
          onClose={() => setShowNew(false)}
          onSaved={async () => {
            setShowNew(false)
            await reload()
          }}
        />
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: FundsProvisionDto['status'] }) {
  const { t } = useTranslation()
  const STYLES = {
    ACTIVE: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    APPLIED: 'bg-slate-500/15 t-secondary border-slate-500/30',
    REFUNDED: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  } as const
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[status]}`}>
      {t('enums:FundsProvisionStatus.' + status)}
    </span>
  )
}

function NewProvisionModal({
  clientId,
  opportunityId,
  onClose,
  onSaved,
}: {
  clientId: string
  opportunityId?: string
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const { t } = useTranslation(['billing', 'common'])
  const [receivedAt, setReceivedAt] = useState(new Date().toISOString().slice(0, 10))
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<PaymentMethodT>('TRANSFERENCIA')
  const [description, setDescription] = useState('')
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const cents = parseEurosToCents(amount)
    if (cents <= 0) {
      setError(t('fundsProvision.invalidAmount'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.post('/funds-provisions', {
        clientId,
        opportunityId: opportunityId ?? null,
        receivedAt: new Date(receivedAt).toISOString(),
        amountCents: cents,
        method,
        description: description || null,
        reference: reference || null,
      })
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('common:errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">{t('fundsProvision.modalTitle')}</h3>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('fundsProvision.fields.date')}</span>
            <input
              type="date"
              value={receivedAt}
              onChange={(e) => setReceivedAt(e.target.value)}
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('fundsProvision.fields.amount')}</span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 font-mono"
              required
            />
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">{t('fundsProvision.fields.method')}</span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethodT)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          >
            {Object.keys(PaymentMethodLabel).map((k) => (
              <option key={k} value={k}>
                {t('enums:PaymentMethod.' + k)}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs t-muted">{t('fundsProvision.fields.description')}</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('fundsProvision.placeholders.description')}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          />
        </label>

        <label className="block">
          <span className="text-xs t-muted">{t('fundsProvision.fields.reference')}</span>
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={t('fundsProvision.placeholders.reference')}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          />
        </label>

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {t('fundsProvision.receive')}
          </button>
        </div>
      </form>
    </div>
  )
}
